import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// References to the @theme tokens in app/globals.css, for JS-side CSS values (style props, SVG attributes).
// Not usable where a literal color is required, e.g. canvas drawing.
export const Colors = {
  orangeAction: "var(--color-orange-action)",
  orangeLight: "var(--color-orange-light)",
  yellowSupport: "var(--color-yellow-support)",
  greenSupport: "var(--color-green-support)",
  bgMain: "var(--color-bg-main)",
  grayHover: "var(--color-gray-hover)",
  graySecondary: "var(--color-gray-secondary)",
  redDanger: "var(--color-red-danger)",
} as const;

// Utility to strip time from a date
export function stripTime(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatDateAsString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function validateEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

// Helper to get platform string from postableType (case-insensitive, dynamic)
export const getSocialPlatform = (type: string | undefined) => {
  const lowerType = type?.toLowerCase() || "";
  if (lowerType.includes("facebook")) return "facebook";
  if (lowerType.includes("instagram")) return "instagram";
  if (lowerType.includes("tiktok")) return "tiktok";
  if (lowerType.includes("youtube")) return "youtube";
  if (lowerType.includes("linkedin")) return "linkedin";
  if (lowerType.includes("threads")) return "threads";
  return undefined;
};

export const cleanUrl = (url: string) => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.endsWith("/") ? urlObj.pathname : `${urlObj.pathname}/`;
    return `${urlObj.origin}${pathname}`;
  } catch {
    return url;
  }
};

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const truncate = (text: string, max_length: number) => {
  const full = text || "";
  const needsTruncate = full.length > max_length;
  return needsTruncate ? `${full.slice(0, max_length)}…` : full;
};

/**
 * Get the appropriate login redirect URL for the app.
 * Always send users to the local login route; the login page itself
 * is responsible for redirecting to the legacy app in production.
 */
export function getLoginRedirectUrl(isV2Logout = false): string {
  if (isV2Logout && process.env.NODE_ENV === "production" && process.env.IS_DEPLOY_PREVIEW !== "true") {
    return "/session/login?v2_logout=true";
  }

  // On the client, include a safe same-origin returnTo using the document.referrer when available.
  // Server paths will fall back to a bare /session/login unless a higher-level helper provides a path.
  if (typeof window !== "undefined") {
    try {
      const ref = document.referrer;
      if (ref) {
        const refUrl = new URL(ref);
        const currentOrigin = window.location.origin;
        const path = `${refUrl.pathname}${refUrl.search || ""}`;

        if (
          refUrl.origin === currentOrigin &&
          path.startsWith("/") &&
          !path.startsWith("//") &&
          !path.startsWith("/session")
        ) {
          return `/session/login?returnTo=${encodeURIComponent(path)}`;
        }
      }
    } catch {
      // ignore invalid referrer values
    }
  }

  return "/session/login";
}

/**
 *
 * @param platform social media platform name
 * @returns boolean indicating if the platform is an ad (false if organic)
 */
export const isAd = (platform: string): boolean => {
  return platform.toLowerCase().includes("_ad") || platform.toLowerCase().includes("/ad_account");
};

export const isPaidSocialPostableType = (postableType: string | null | undefined): boolean => {
  const normalizedPostableType = postableType?.toLowerCase() ?? "";
  return normalizedPostableType === "tiktok::ad" || normalizedPostableType === "facebook::adcreative";
};

export const supportsRespondManualLike = (
  platform: string | null | undefined,
  postableType: string | null | undefined,
): boolean => {
  const resolvedPlatform = getSocialPlatform(platform ?? undefined) ?? getSocialPlatform(postableType ?? undefined);

  if (resolvedPlatform === "facebook") {
    return true;
  }

  if (resolvedPlatform !== "tiktok") {
    return false;
  }

  return !isPaidSocialPostableType(postableType) && !(platform ? isAd(platform) : false);
};

export const ensureHttps = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith("http://localhost")) {
    return url;
  }
  if (url.startsWith("http://")) {
    return url.replace("http://", "https://");
  }
  if (url.startsWith("https://")) {
    return url;
  }
  if (url.startsWith("localhost")) {
    return `http://${url}`;
  }
  return `https://${url}`;
};
